import {cn} from '@sollapay/ui';
import {HeaderMetaRow, PageHeader, Skeleton, SplitButtonSkeleton} from '@sollapay/ui/components';
import {useGoBack, useGoForward} from '@sollapay/web-core';
import {useTranslation} from 'react-i18next';

import {TrustAccountBreadcrumbs} from '@/features/trust-accounts/components';

import {TrustAccountViewBalanceMetricSkeleton} from './TrustAccountViewBalanceMetric';
import {TrustAccountViewHeaderActions} from './TrustAccountViewHeaderActions';
import {TrustAccountViewTabs} from './TrustAccountViewTabs';

import type {TrustAccountViewTab} from '../utils';
import type {HeaderMetaEntry} from '@sollapay/ui/components';
import type {FC} from 'react';

export interface TrustAccountViewHeaderSkeletonProps {
  activeTab: TrustAccountViewTab;
  onTabChange: (tab: TrustAccountViewTab) => void;
}

const META_ITEM_WIDTHS = ['w-10.5', 'w-25', 'w-17', 'w-24.5'] as const;

const META_ITEMS: HeaderMetaEntry[] = [
  {
    key: 'account-number',
    custom: (
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-5.5 rounded" />
        <Skeleton className="h-4 w-17" />
        <Skeleton className="h-6 w-32.5 rounded-md" />
      </div>
    )
  },
  ...META_ITEM_WIDTHS.map(width => ({
    key: width,
    custom: (
      <div className="flex items-center gap-2">
        <Skeleton className="size-4 rounded" />
        <Skeleton className={cn('h-4', width)} />
      </div>
    )
  }))
];

export const TrustAccountViewHeaderSkeleton: FC<TrustAccountViewHeaderSkeletonProps> = ({
  activeTab,
  onTabChange
}) => {
  const {t} = useTranslation('trustAccounts');
  const back = useGoBack();
  const forward = useGoForward();

  return (
    <PageHeader
      data-slot="trust-account-view-header-skeleton"
      title=""
      titleNode={<Skeleton className="h-9.5 w-72" />}
      back={back}
      forward={forward}
      description={<HeaderMetaRow wrap items={META_ITEMS} />}
      badge={<Skeleton className="h-7 w-14.5 rounded-full" />}
      actionsBesideTabs
      actions={
        <TrustAccountViewHeaderActions
          button={<SplitButtonSkeleton />}
          isBalanceHidden={activeTab === 'overview'}
          balance={<TrustAccountViewBalanceMetricSkeleton />}
        />
      }
      breadcrumbs={<TrustAccountBreadcrumbs title={t(`view.tabs.${activeTab}`)} />}
      tabs={
        <TrustAccountViewTabs
          buyersCount={0}
          transactionsCount={0}
          documentsCount={0}
          beneficiariesCount={0}
          payoutsCount={0}
          paymentInstructionsCount={0}
          value={activeTab}
          onValueChange={onTabChange}
        />
      }
    />
  );
};
